import React, { useMemo, useCallback } from 'react';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import type { TelemetrySample } from '@/types/telemetry';
import type { TrackShape, TrackAnnotation, TrackPoint } from '@/lib/spline';
import { sampleClosedSpline, findClosestParametricT, getPointAtT } from '@/lib/spline';
import { projectToLocalMeters, findClosestPointOnPolyline, localMetersToGeo } from '@/lib/map/mapGeometry';
import type { Point2D, GeoCoord } from '@/lib/map/mapGeometry';

export interface SectorTrackMapProps {
  samples: TelemetrySample[];
  trackShape?: TrackShape | null;
  trackAnnotations?: TrackAnnotation[];
  splitPositions: number[];
  splitPoints?: TrackPoint[];
  onSplitsChange?: (splits: number[], splitPoints?: TrackPoint[]) => void;
  readonly?: boolean;
  drawRibbonFill?: boolean;
  className?: string;
}

export const SectorTrackMap: React.FC<SectorTrackMapProps> = ({
  samples,
  trackShape,
  trackAnnotations,
  splitPositions,
  splitPoints,
  onSplitsChange,
  readonly = false,
  drawRibbonFill,
  className = '',
}) => {
  const hasShape = !!(trackShape && trackShape.centerline && trackShape.centerline.length >= 3);
  const trackWidthM = trackShape?.trackWidthM || 8.0;

  const splineSamples = useMemo(() => {
    if (!hasShape) return [];
    return sampleClosedSpline(trackShape!.centerline, trackWidthM, 30);
  }, [hasShape, trackShape, trackWidthM]);

  const finishLineAnnot = useMemo(() => {
    if (!hasShape) return null;
    return (trackAnnotations || []).find(a => a.type === 'finish_line') || null;
  }, [hasShape, trackAnnotations]);

  const finishT = useMemo(() => {
    if (!hasShape || !finishLineAnnot?.location || splineSamples.length === 0) return 0.0;
    return findClosestParametricT(finishLineAnnot.location, splineSamples).t;
  }, [hasShape, finishLineAnnot, splineSamples]);

  const referencePoints = useMemo(() => {
    return samples
      .filter(s => s.latitudeRaw && s.longitudeRaw)
      .map(s => ({ lat: s.latitudeRaw / 1e6, lng: s.longitudeRaw / 1e6 }));
  }, [samples]);

  const sortedSplits = useMemo(() => {
    return [...splitPositions].filter(p => p > 0 && p < 1).sort((a, b) => a - b);
  }, [splitPositions]);

  const bounds = useMemo(() => [0.0, ...sortedSplits, 1.0], [sortedSplits]);
  const numSectors = bounds.length - 1;

  function sectorHue(sectorIdx: number, totalSectors: number): number {
    return (sectorIdx * 360 / totalSectors) % 360;
  }

  const getMarkerGeo = useCallback(
    (pos: number): TrackPoint => {
      if (hasShape && splineSamples.length > 0) {
        const t = (finishT + pos) % 1.0;
        return getPointAtT(t, trackShape!.centerline);
      }
      const idx = Math.min(Math.max(0, Math.round(pos * (referencePoints.length - 1))), referencePoints.length - 1);
      return referencePoints[idx] || { lat: 0, lng: 0 };
    },
    [hasShape, splineSamples, finishT, trackShape, referencePoints]
  );

  const [isNearTrack, setIsNearTrack] = React.useState(false);
  const splitCanvasPtsRef = React.useRef<{ id: string; x: number; y: number }[]>([]);

  // Overlay rendering: draw sector colors along centerline and split pins
  const renderOverlay = useCallback(
    (ctx: CanvasRenderingContext2D, { toCanvas, anchor }: OverlayUtils) => {
      const project = (lat: number, lng: number) => {
        return toCanvas(projectToLocalMeters({ lat, lng }, anchor));
      };

      if (hasShape && splineSamples.length > 0) {
        // Draw colored sectors along track centerline
        const totalSamples = splineSamples.length;
        for (let si = 0; si < numSectors; si++) {
          const startRel = bounds[si];
          const endRel = bounds[si + 1];
          const hue = sectorHue(si, numSectors);

          ctx.strokeStyle = `hsl(${hue}, 85%, 55%)`;
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();

          const numSteps = Math.max(8, Math.round((endRel - startRel) * totalSamples));
          for (let step = 0; step <= numSteps; step++) {
            const u = step / numSteps;
            const relT = startRel + u * (endRel - startRel);
            const t = (finishT + relT) % 1.0;
            const pt = getPointAtT(t, trackShape!.centerline);
            const cp = project(pt.lat, pt.lng);
            if (step === 0) ctx.moveTo(cp.x, cp.y);
            else ctx.lineTo(cp.x, cp.y);
          }
          ctx.stroke();
        }
      } else if (referencePoints.length > 1) {
        // Fallback trace colored by sectors
        for (let si = 0; si < numSectors; si++) {
          const startIdx = Math.round(bounds[si] * (referencePoints.length - 1));
          const endIdx = Math.round(bounds[si + 1] * (referencePoints.length - 1));
          const hue = sectorHue(si, numSectors);

          ctx.strokeStyle = `hsl(${hue}, 85%, 55%)`;
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();

          for (let i = startIdx; i <= endIdx; i++) {
            const cp = project(referencePoints[i].lat, referencePoints[i].lng);
            if (i === startIdx) ctx.moveTo(cp.x, cp.y);
            else ctx.lineTo(cp.x, cp.y);
          }
          ctx.stroke();
        }
      }

      // Draw Sector Start Markers (1..numSectors) & cache split canvas coordinates for hit testing
      const canvasSplits: { id: string; x: number; y: number }[] = [];
      for (let si = 0; si < numSectors; si++) {
        const pos = bounds[si];
        const pt = getMarkerGeo(pos);
        const cp = project(pt.lat, pt.lng);
        if (si > 0) {
          canvasSplits.push({ id: `split-${si - 1}`, x: cp.x, y: cp.y });
        }
        const hue = sectorHue(si, numSectors);

        // Halo
        ctx.beginPath();
        ctx.arc(cp.x, cp.y, 14, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${hue}, 85%, 55%, 0.25)`;
        ctx.fill();

        // Pin body
        ctx.beginPath();
        ctx.arc(cp.x, cp.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${hue}, 85%, 55%)`;
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Sector number at start of sector
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(si + 1), cp.x, cp.y);
      }
      splitCanvasPtsRef.current = canvasSplits;
    },
    [hasShape, splineSamples, numSectors, bounds, finishT, trackShape, referencePoints, getMarkerGeo]
  );

  // Hit test for split markers (canvas distance <= 18px)
  const hitTestInteractive = useCallback(
    (canvasPt: Point2D) => {
      if (readonly) return null;
      for (let i = 0; i < splitCanvasPtsRef.current.length; i++) {
        const sp = splitCanvasPtsRef.current[i];
        const dist = Math.hypot(canvasPt.x - sp.x, canvasPt.y - sp.y);
        if (dist <= 18) {
          return sp.id; // 'split-0', 'split-1', etc.
        }
      }
      return null;
    },
    [readonly]
  );

  // Drag handler to slide split pins along the track line
  const onDragHandle = useCallback(
    (handleId: string, metricPt: Point2D) => {
      if (readonly || !onSplitsChange || !handleId.startsWith('split-')) return;
      const splitIdx = parseInt(handleId.replace('split-', ''), 10);
      if (isNaN(splitIdx) || splitIdx < 0 || splitIdx >= sortedSplits.length) return;

      let newRelPos: number | null = null;

      if (hasShape && splineSamples.length > 0) {
        const anchorCoord = { lat: trackShape!.centerline[0].lat, lng: trackShape!.centerline[0].lng };
        const geoPt = localMetersToGeo(metricPt, anchorCoord);
        const { t, distanceToCurveM } = findClosestParametricT(geoPt, splineSamples);
        if (distanceToCurveM <= 60) {
          newRelPos = ((t - finishT) % 1.0 + 1.0) % 1.0;
        }
      } else if (referencePoints.length > 1) {
        const anchorCoord = referencePoints[0];
        const refMetrics = referencePoints.map(p => projectToLocalMeters(p, anchorCoord));
        const { progressIndex, distSq } = findClosestPointOnPolyline(metricPt, refMetrics);
        if (distSq <= 3600) {
          newRelPos = progressIndex / (referencePoints.length - 1);
        }
      }

      if (newRelPos !== null && newRelPos > 0.01 && newRelPos < 0.99) {
        const updatedSplits = [...sortedSplits];
        updatedSplits[splitIdx] = newRelPos;
        const nextSplits = [...updatedSplits].sort((a, b) => a - b);
        const nextPts = nextSplits.map(pos => getMarkerGeo(pos));
        onSplitsChange(nextSplits, nextPts);
      }
    },
    [readonly, onSplitsChange, sortedSplits, hasShape, splineSamples, trackShape, finishT, referencePoints, getMarkerGeo]
  );

  const handleClick = useCallback(
    (_canvasPt: Point2D, metricPt: Point2D, geoPt: GeoCoord) => {
      if (readonly || !onSplitsChange || sortedSplits.length >= 6) return;

      let relPos: number | null = null;

      if (hasShape && splineSamples.length > 0) {
        const { t, distanceToCurveM } = findClosestParametricT(geoPt, splineSamples);
        if (distanceToCurveM <= 45) { // within 45m radius
          relPos = ((t - finishT) % 1.0 + 1.0) % 1.0;
        }
      } else if (referencePoints.length > 1) {
        const anchorCoord = referencePoints[0];
        const refMetrics = referencePoints.map(p => projectToLocalMeters(p, anchorCoord));
        const { progressIndex, distSq } = findClosestPointOnPolyline(metricPt, refMetrics);
        if (distSq <= 2025) { // 45m radius
          relPos = progressIndex / (referencePoints.length - 1);
        }
      }

      if (relPos !== null && relPos > 0.02 && relPos < 0.98) {
        const tooClose = sortedSplits.some(s => Math.abs(s - relPos!) < 0.02);
        if (!tooClose) {
          const nextSplits = [...sortedSplits, relPos].sort((a, b) => a - b);
          const nextPts = nextSplits.map(pos => getMarkerGeo(pos));
          onSplitsChange(nextSplits, nextPts);
        }
      }
    },
    [readonly, onSplitsChange, sortedSplits, hasShape, splineSamples, finishT, referencePoints, getMarkerGeo]
  );

  const handleHover = useCallback(
    (_canvasPt: Point2D | null, metricPt: Point2D | null, geoPt: GeoCoord | null) => {
      if (readonly || !geoPt || !metricPt) {
        setIsNearTrack(false);
        return;
      }
      if (hasShape && splineSamples.length > 0) {
        const { distanceToCurveM } = findClosestParametricT(geoPt, splineSamples);
        setIsNearTrack(distanceToCurveM <= 35);
      } else if (referencePoints.length > 1) {
        const anchorCoord = referencePoints[0];
        const refMetrics = referencePoints.map(p => projectToLocalMeters(p, anchorCoord));
        const { distSq } = findClosestPointOnPolyline(metricPt, refMetrics);
        setIsNearTrack(distSq <= 1225); // 35m radius
      } else {
        setIsNearTrack(false);
      }
    },
    [readonly, hasShape, splineSamples, referencePoints]
  );

  return (
    <div className={`relative aspect-square w-full mx-auto overflow-hidden rounded-2xl shadow-2xl border border-white/10 ${className}`}>
      <InteractiveTrackMap
        trackShape={trackShape}
        trackAnnotations={trackAnnotations}
        points={referencePoints}
        renderOverlay={renderOverlay}
        hitTestInteractive={hitTestInteractive}
        onDragHandle={onDragHandle}
        onClick={handleClick}
        onHover={handleHover}
        drawRibbonFill={drawRibbonFill ?? !readonly}
        cursor={isNearTrack ? 'crosshair' : undefined}
        className="w-full h-full"
      />
    </div>
  );
};
